import {
    type ChangeEvent,
    type ClipboardEvent,
    type KeyboardEvent,
    useId,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { useFormFieldControl } from '@/components/atoms/FormField';
import { RemovableChip } from '@/components/atoms/RemovableChip';

import { chipKeys, matchesPattern, splitChipInput } from './splitChipInput';

import styles from './ChipListInput.module.scss';

/** A list box for use inside a FormField, which gives it its label, error and id. */
export interface ChipListInputProps {
    id?: string;
    values: string[];
    onChange: (values: string[]) => void;
    itemPattern?: RegExp;
    maxItems?: number;
    disabled?: boolean;
    placeholder?: string;
    required?: boolean;
    /** For the typed text and each chip's value, such as monospace for ids. */
    valueClassName?: string;
}

// Mobile keyboards often report the comma key as "Unidentified", so a separator
// can only be seen once it lands in the value.
const TYPED_SEPARATOR = /[,;\n]/;

function ChipListInput({
    id,
    values,
    onChange,
    itemPattern,
    maxItems,
    disabled = false,
    placeholder,
    required = false,
    valueClassName,
}: ChipListInputProps) {
    const { t } = useTranslation('atoms');
    const [draft, setDraft] = useState('');
    const [invalidItem, setInvalidItem] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const isFull = maxItems !== undefined && values.length >= maxItems;
    // useId's colons are valid in an id but not in a CSS selector, which jsdom and tests use.
    const generatedId = useId().replace(/:/g, '');
    const fieldId = useFormFieldControl({ id }).id ?? generatedId;
    const invalidItemId = `${fieldId}-invalid-item`;
    // The typed item's own problem first, then the field's hint and error.
    const control = useFormFieldControl({
        id: fieldId,
        invalid: invalidItem === null ? undefined : true,
        'aria-describedby': invalidItem === null ? undefined : invalidItemId,
    });

    function commit(text: string): void {
        const next = [...values];
        const leftOver: string[] = [];
        let firstInvalid: string | null = null;
        for (const item of splitChipInput(text)) {
            if (!matchesPattern(item, itemPattern)) {
                firstInvalid ??= item;
                leftOver.push(item);
            } else if (!next.includes(item)) {
                if (maxItems !== undefined && next.length >= maxItems) leftOver.push(item);
                else next.push(item);
            }
        }
        if (next.length !== values.length) onChange(next);
        setDraft(leftOver.join(' '));
        setInvalidItem(firstInvalid);
    }

    function handleChange(event: ChangeEvent<HTMLInputElement>): void {
        const text = event.target.value;
        if (TYPED_SEPARATOR.test(text)) {
            commit(text);
            return;
        }
        setDraft(text);
        setInvalidItem(null);
    }

    function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
        if (event.key === 'Enter' || event.key === ',') {
            // Enter would otherwise submit the surrounding form with the item uncommitted.
            event.preventDefault();
            commit(draft);
        } else if (event.key === 'Backspace' && draft === '' && values.length > 0) {
            onChange(values.slice(0, -1));
        }
    }

    function handlePaste(event: ClipboardEvent<HTMLInputElement>): void {
        event.preventDefault();
        const pasted = event.clipboardData.getData('text');
        const input = event.currentTarget;
        const start = input.selectionStart ?? draft.length;
        const end = input.selectionEnd ?? draft.length;
        commit(`${draft.slice(0, start)}${pasted}${draft.slice(end)}`);
    }

    function removeAt(index: number): void {
        onChange(values.filter((_, position) => position !== index));
        // The clicked button unmounts with its chip; without this, focus falls to the body.
        inputRef.current?.focus();
    }

    const keys = chipKeys(values);

    return (
        <div className={styles.chipListInput}>
            <div
                className={classNames(styles.field, {
                    [styles.fieldInvalid]: control['aria-invalid'] === true,
                    [styles.fieldDisabled]: disabled,
                })}>
                {values.length > 0 && (
                    <ul className={styles.chips}>
                        {values.map((item, index) => (
                            <RemovableChip
                                key={keys[index]}
                                removeLabel={t('chipListInput.remove', { item })}
                                onRemove={() => removeAt(index)}
                                disabled={disabled}
                                valueClassName={valueClassName}>
                                {item}
                            </RemovableChip>
                        ))}
                    </ul>
                )}
                <input
                    ref={inputRef}
                    {...control}
                    className={classNames(styles.input, valueClassName)}
                    type="text"
                    autoComplete="off"
                    value={draft}
                    placeholder={
                        values.length === 0
                            ? (placeholder ?? t('chipListInput.placeholder'))
                            : undefined
                    }
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    onBlur={() => commit(draft)}
                    disabled={disabled}
                    readOnly={isFull && draft === ''}
                    aria-required={required || undefined}
                />
            </div>
            {/* Always mounted: a live region added together with its text is not announced. */}
            <div aria-live="polite">
                {invalidItem !== null && (
                    <p id={invalidItemId} className={styles.invalidItem}>
                        {t('chipListInput.invalidItem', { item: invalidItem })}
                    </p>
                )}
            </div>
        </div>
    );
}

export default ChipListInput;
export { ChipListInput };
